import { useCallback, useMemo, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import { SUPPORTED_LANGUAGES } from '../../i18n';
import { isValidProjectId } from '../../utils/projectIds';
import { getErrorMessage } from '../../utils/errors';
import { getTranslationStatusLabel } from '../../utils/i18nHelpers';
import { compareProjectFilesByOrder } from '../../../shared/projectFileOrder';
import './TranslationStudio.css';

const chapterName = (path) => (path.split('/').pop() || path).replace(/\.md$/i, '');

const TARGET_LANGUAGES = SUPPORTED_LANGUAGES;

// Translation style options by language - labels show "English (Native)" format
const TRANSLATION_STYLES = {
  // English styles
  en: [
    { id: 'standard', label: 'Standard', description: 'Neutral, formal English' },
    { id: 'american-southern', label: 'American Southern', description: 'Louisiana/Southern dialect' },
    { id: 'american-nyc', label: 'New York Style', description: 'NYC urban dialect' },
    { id: 'british', label: 'British English', description: 'UK spelling and expressions' },
    { id: 'indian-mumbai', label: 'Indian Mumbai', description: 'Mumbai Hindi-English mix' },
    { id: 'indian-goan', label: 'Indian Goan', description: 'Goan Portuguese-influenced' },
  ],
  // Hebrew styles - English (עברית)
  he: [
    { id: 'standard', label: 'Standard (סטנדרטי)', description: 'Standard Hebrew' },
    { id: 'mizrahi', label: 'Mizrahi (מזרחי)', description: 'Middle Eastern style' },
    { id: 'ashkenazi', label: 'Ashkenazi (אשכנזי)', description: 'European Jewish style' },
    { id: 'arab-israeli', label: 'Arab-Israeli (ערבי-ישראלי)', description: 'Arabic influences' },
    { id: 'slang', label: 'Slang (סלנג)', description: 'Modern Israeli slang' },
  ],
  // Spanish styles - English (Español)
  es: [
    { id: 'standard', label: 'Standard (Estándar)', description: 'Neutral Spanish' },
    { id: 'mexican', label: 'Mexican (Mexicano)', description: 'Mexican expressions' },
    { id: 'argentinian', label: 'Argentinian (Argentino)', description: 'Voseo style' },
    { id: 'caribbean', label: 'Caribbean (Caribeño)', description: 'Caribbean Spanish' },
  ],
  // Arabic styles - English (عربي)
  ar: [
    { id: 'standard', label: 'Standard (فصحى)', description: 'Modern Standard Arabic' },
    { id: 'egyptian', label: 'Egyptian (مصري)', description: 'Egyptian dialect' },
    { id: 'levantine', label: 'Levantine (شامي)', description: 'Syrian/Lebanese dialect' },
    { id: 'gulf', label: 'Gulf (خليجي)', description: 'Gulf region dialect' },
  ],
  // French styles - English (Français)
  fr: [
    { id: 'standard', label: 'Standard', description: 'Standard French' },
    { id: 'canadian', label: 'Canadian (Québécois)', description: 'Canadian French' },
    { id: 'african-fr', label: 'African (Africain)', description: 'African French' },
  ],
  // Portuguese styles - English (Português)
  pt: [
    { id: 'standard', label: 'Standard (Padrão)', description: 'Standard Portuguese' },
    { id: 'brazilian', label: 'Brazilian (Brasileiro)', description: 'Brazilian Portuguese' },
    { id: 'african-pt', label: 'African (Africano)', description: 'African Portuguese' },
  ],
  // Hindi styles - English (हिंदी)
  hi: [
    { id: 'standard', label: 'Standard (मानक)', description: 'Standard Hindi' },
    { id: 'mumbai', label: 'Mumbai (मुंबई)', description: 'Bambaiya Hindi' },
    { id: 'delhi', label: 'Delhi (दिल्ली)', description: 'Delhi Hindi style' },
  ],
  // Default for languages without specific styles
  default: [
    { id: 'standard', label: 'Standard', description: 'Standard translation' },
    { id: 'formal', label: 'Formal', description: 'Professional tone' },
    { id: 'casual', label: 'Casual', description: 'Conversational tone' },
    { id: 'literary', label: 'Literary', description: 'Poetic, literary style' },
  ],
};

const getStylesForLanguage = (langCode) => TRANSLATION_STYLES[langCode] || TRANSLATION_STYLES.default;

function TranslationStudioPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const safeProjectId = isValidProjectId(projectId) ? projectId : null;

  const [targetLang, setTargetLang] = useState('es');
  const [translationStyle, setTranslationStyle] = useState('standard');
  const [expandedChapter, setExpandedChapter] = useState(null);
  const [translatingPaths, setTranslatingPaths] = useState(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get available styles for current language
  const availableStyles = getStylesForLanguage(targetLang);

  // Reset style to standard when language changes if current style not available
  const handleLanguageChange = (newLang) => {
    setTargetLang(newLang);
    const newStyles = getStylesForLanguage(newLang);
    if (!newStyles.find(s => s.id === translationStyle)) {
      setTranslationStyle('standard');
    }
  };

  const project = useQuery(api.projects.getById, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const files = useQuery(api.files.listByProject, safeProjectId ? { projectId: safeProjectId } : 'skip');
  const studioData = useQuery(api.translations.getStudio, safeProjectId ? { projectId: safeProjectId, targetLanguage: targetLang } : 'skip');

  const translateChapters = useAction(api.translations.translateChapters);
  const retranslateChapter = useAction(api.translations.retranslateChapter);
  const deleteLanguageDrafts = useMutation(api.translations.deleteLanguageDrafts);
  const saveConfig = useMutation(api.translations.saveConfig);

  const chapters = useMemo(() => {
    if (!files) return [];
    return files
      .filter((f) => f.type === 'file' && f.path.startsWith('Chapters/') && f.content?.trim())
      .sort(compareProjectFilesByOrder);
  }, [files]);

  // Build translation lookup for current language
  const translationMap = useMemo(() => {
    const map = {};
    if (studioData?.translations) {
      for (const tr of studioData.translations) {
        if (tr.targetLanguage === targetLang) {
          map[tr.chapterPath] = tr;
        }
      }
    }
    return map;
  }, [studioData, targetLang]);

  const langLabel = TARGET_LANGUAGES.find((l) => l.code === targetLang)?.nativeName || targetLang;

  // ── Actions ──

  const handleTranslateAll = useCallback(async () => {
    if (!safeProjectId || chapters.length === 0) return;
    const paths = chapters.map((c) => c.path);
    setTranslatingPaths(new Set(paths));
    setError('');
    try {
      await saveConfig({ projectId: safeProjectId, targetLanguage: targetLang, scopeMode: 'full', selectedChapterPaths: paths });
      await translateChapters({ projectId: safeProjectId, targetLanguage: targetLang, chapterPaths: paths, style: translationStyle });
      setSuccess(t('translationStudio.allTranslated'));
    } catch (err) {
      setError(getErrorMessage(err, t('translationStudio.translationFailed')));
    } finally {
      setTranslatingPaths(new Set());
    }
  }, [safeProjectId, chapters, targetLang, translationStyle, translateChapters, saveConfig, t]);

  const handleTranslateOne = useCallback(async (chapterPath) => {
    if (!safeProjectId) return;
    setTranslatingPaths((prev) => new Set([...prev, chapterPath]));
    setError('');
    try {
      await retranslateChapter({ projectId: safeProjectId, targetLanguage: targetLang, chapterPath, style: translationStyle });
      setSuccess(t('translationStudio.chapterTranslated', { chapter: chapterName(chapterPath) }));
    } catch (err) {
      setError(getErrorMessage(err, t('translationStudio.translationFailed')));
    } finally {
      setTranslatingPaths((prev) => { const next = new Set(prev); next.delete(chapterPath); return next; });
    }
  }, [safeProjectId, targetLang, translationStyle, retranslateChapter]);

  const handleCopy = useCallback((content) => {
    navigator.clipboard.writeText(content);
    setSuccess(t('common.copiedToClipboard'));
    setTimeout(() => setSuccess(''), 2000);
  }, [t]);

  const handleDownload = useCallback((path, content) => {
    const name = `${chapterName(path)}.${targetLang}.md`;
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }, [targetLang]);

  const handleExportAll = useCallback(() => {
    if (!studioData?.translations) return;
    const langTranslations = studioData.translations
      .filter((tr) => tr.targetLanguage === targetLang && tr.translatedContent)
      .sort((a, b) => a.chapterPath.localeCompare(b.chapterPath));
    if (langTranslations.length === 0) return;
    const combined = langTranslations.map((tr) => `# ${chapterName(tr.chapterPath)}\n\n${tr.translatedContent}`).join('\n\n---\n\n');
    const blob = new Blob([combined], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${project?.title || 'book'}-${targetLang}.md`; a.click();
    URL.revokeObjectURL(url);
  }, [studioData, targetLang, project?.title]);

  const handleDeleteLanguage = useCallback(async () => {
    if (!safeProjectId || !confirm(t('translationStudio.confirmDelete', 'Delete all translations for this language?'))) return;
    try {
      await deleteLanguageDrafts({ projectId: safeProjectId, targetLanguage: targetLang });
      setExpandedChapter(null);
      setSuccess(t('translationStudio.translationsDeleted'));
    } catch (err) {
      setError(getErrorMessage(err, t('translationStudio.deleteFailed')));
    }
  }, [safeProjectId, targetLang, deleteLanguageDrafts, t]);

  const handleExpand = useCallback((path) => {
    setExpandedChapter((prev) => prev === path ? null : path);
    setError('');
    setSuccess('');
  }, []);

  // ── Guards ──
  if (!safeProjectId) return <div className="ts-page"><p>{t('translationStudio.invalidProject')}</p></div>;
  if (project === undefined || files === undefined) return <div className="ts-page"><div className="ts-loading">{t('translationStudio.loadingStudio')}</div></div>;
  if (project === null) return <div className="ts-page"><p>{t('common.projectNotFound')}</p></div>;

  const translatedCount = Object.values(translationMap).filter((tr) => tr.status === 'translated').length;

  return (
    <div className="ts-page">
      {/* Header */}
      <div className="ts-header">
        <div className="ts-header-left">
          <h1 className="ts-title">
            {t('translationStudio.title', 'Translation Studio')}
            <span className="ts-title-project"> — {project.title}</span>
          </h1>
          <p className="ts-subtitle">{t('translationStudio.description', 'Translate your chapters into 120+ languages with AI-powered translation.')}</p>
        </div>
        <div className="ts-header-actions">
          <button className="ts-btn ts-btn--ghost" onClick={() => navigate(`/projects/${projectId}`)}>{t('common.back')}</button>
          <button className="ts-btn ts-btn--ghost" onClick={() => navigate(`/projects/${projectId}`)}>{t('common.editor')}</button>
          <button className="ts-btn ts-btn--primary" onClick={handleExportAll} disabled={translatedCount === 0}>{t('translationStudio.exportAll')}</button>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="ts-alert ts-alert--error">{error}</div>}
      {success && <div className="ts-alert ts-alert--success">{success}</div>}

      {/* Controls bar */}
      <div className="ts-controls">
        <div className="ts-controls-left">
          <div className="ts-control-group">
            <label className="ts-label">{t('translationStudio.selectLanguage', 'Target language')}</label>
            <select className="ts-select" value={targetLang} onChange={(e) => handleLanguageChange(e.target.value)}>
              {TARGET_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>{lang.nativeName} ({lang.name})</option>
              ))}
            </select>
          </div>
          <div className="ts-control-group">
            <label className="ts-label">{t('translationStudio.selectStyle', 'Translation style')}</label>
            <select
              className="ts-select"
              value={translationStyle}
              onChange={(e) => setTranslationStyle(e.target.value)}
              title={availableStyles.find(s => s.id === translationStyle)?.description}
            >
              {availableStyles.map((style) => (
                <option key={style.id} value={style.id} title={style.description}>
                  {style.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="ts-controls-right">
          <span className="ts-progress">{t('translationStudio.progress', { translated: translatedCount, total: chapters.length })}</span>
          <button
            className="ts-btn ts-btn--primary"
            onClick={handleTranslateAll}
            disabled={translatingPaths.size > 0 || chapters.length === 0}
          >
            {translatingPaths.size > 0
              ? t('translationStudio.translatingCount', { count: translatingPaths.size })
              : t('translationStudio.translateAllTo', { language: langLabel })}
          </button>
          <button className="ts-btn ts-btn--danger" onClick={handleDeleteLanguage} disabled={translatedCount === 0}>
            {t('translationStudio.deleteLanguageLabel', { language: langLabel })}
          </button>
        </div>
      </div>

      {/* Chapter list */}
      {chapters.length === 0 ? (
        <div className="ts-empty">{t('translationStudio.noChaptersFound', 'No chapters found. Add chapters in the Writing Studio first.')}</div>
      ) : (
        <div className="ts-chapters">
          {chapters.map((ch) => {
            const tr = translationMap[ch.path];
            const isExpanded = expandedChapter === ch.path;
            const isTranslating = translatingPaths.has(ch.path);
            const status = isTranslating ? 'translating' : (tr?.status || 'untranslated');

            return (
              <div key={ch.path} className={`ts-chapter ${isExpanded ? 'ts-chapter--expanded' : ''}`}>
                <div className="ts-chapter-row" onClick={() => handleExpand(ch.path)}>
                  <div className="ts-chapter-info">
                    <span className="ts-chapter-name">{chapterName(ch.path)}</span>
                    <span className={`ts-badge ts-badge--${status}`}>{getTranslationStatusLabel(t, status)}</span>
                  </div>
                  <div className="ts-chapter-actions" onClick={(e) => e.stopPropagation()}>
                    {tr?.translatedContent && (
                      <>
                        <button className="ts-btn ts-btn--sm" onClick={() => handleCopy(tr.translatedContent)}>{t('translationStudio.copy')}</button>
                        <button className="ts-btn ts-btn--sm" onClick={() => handleDownload(ch.path, tr.translatedContent)}>{t('translationStudio.download')}</button>
                      </>
                    )}
                    <button
                      className="ts-btn ts-btn--sm ts-btn--primary"
                      onClick={() => handleTranslateOne(ch.path)}
                      disabled={isTranslating}
                    >
                      {isTranslating ? '...' : (tr ? t('translationStudio.retranslate') : t('translationStudio.translate'))}
                    </button>
                  </div>
                  <span className="ts-chapter-chevron">{isExpanded ? '▲' : '▼'}</span>
                </div>

                {isExpanded && (
                  <div className="ts-chapter-detail">
                    {tr?.status === 'stale' && (
                      <div className="ts-stale-banner">
                        ⚠️ {t('translationStudio.staleWarning', 'Source has changed since this translation. Consider retranslating.')}
                      </div>
                    )}
                    <div className="ts-detail-panels">
                      <div className="ts-detail-panel">
                        <div className="ts-detail-label">{t('translationStudio.sourceContent', 'Source')}</div>
                        <pre className="ts-detail-content ts-detail-content--source">{ch.content}</pre>
                      </div>
                      <div className="ts-detail-panel">
                        <div className="ts-detail-label">{t('translationStudio.translatedContent', 'Translated')}</div>
                        {tr?.translatedContent ? (
                          <pre className="ts-detail-content ts-detail-content--source">{tr.translatedContent}</pre>
                        ) : (
                          <div className="ts-detail-empty">
                            {isTranslating ? t('translationStudio.translating') : t('translationStudio.notYetTranslated')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TranslationStudioPage;
